import { useState, useEffect } from "react";
import { apiClient, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Shield, Flag, EyeOff, Eye, Trash2, Edit2, CheckCircle2, XCircle, MessageSquare, FileText, Calendar, User } from "lucide-react";
import { format, parseISO } from "date-fns";
import { Navigate } from "react-router-dom";

export default function ModerationPage() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("pending");
  const [confirmAction, setConfirmAction] = useState(null); // { report, action, editContent?, editTitle? }
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (user?.is_moderator) fetchReports(tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, user?.is_moderator]);

  if (user && !user.is_moderator) return <Navigate to="/settings/general" replace />;

  const fetchReports = async (status) => {
    setLoading(true);
    try {
      const r = await apiClient.get(`/moderation/reports?status=${status}`);
      setReports(r.data || []);
    } catch {
      toast.error("Failed to load reports");
    }
    setLoading(false);
  };

  const openConfirm = (report, action) => {
    setReason("");
    setConfirmAction({
      report,
      action,
      editContent: report.target?.content || "",
      editTitle: report.target?.title || ""
    });
  };

  const applyAction = async () => {
    if (!confirmAction) return;
    const { report, action, editContent, editTitle } = confirmAction;
    const url = report.target_type === "post"
      ? `/moderation/posts/${report.target_id}`
      : `/moderation/comments/${report.target_id}`;
    const body = { action, reason };
    if (action === "edit") {
      body.edit = { content: editContent };
      if (report.target_type === "post") body.edit.title = editTitle;
    }
    try {
      await apiClient.post(url, body);
      await apiClient.put(`/moderation/reports/${report.id}/resolve`, { status: "resolved" });
      toast.success(`${action.charAt(0).toUpperCase() + action.slice(1)} applied`);
      setConfirmAction(null);
      fetchReports(tab);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Action failed");
    }
  };

  const dismissReport = async (reportId) => {
    try {
      await apiClient.put(`/moderation/reports/${reportId}/resolve`, { status: "dismissed" });
      toast.success("Report dismissed");
      fetchReports(tab);
    } catch {
      toast.error("Could not dismiss");
    }
  };

  if (!user) return null;

  return (
    <div className="space-y-6" data-testid="moderation-page">
      <div className="flex items-center gap-3">
        <div className="h-11 w-11 rounded-2xl bg-primary/10 flex items-center justify-center">
          <Shield className="h-6 w-6 text-primary" />
        </div>
        <div>
          <h1 className="font-heading text-3xl font-bold text-primary">Moderation Queue</h1>
          <p className="text-muted-foreground mt-0.5 text-sm">Review reported posts and comments</p>
        </div>
      </div>

      <div className="flex gap-2">
        {["pending", "resolved", "dismissed"].map(t => (
          <Button
            key={t}
            variant={tab === t ? "default" : "outline"}
            size="sm"
            className="rounded-full capitalize"
            onClick={() => setTab(t)}
            data-testid={`mod-tab-${t}`}
          >
            {t}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="space-y-3 animate-pulse">
          {[1, 2, 3].map(i => <div key={i} className="h-32 bg-muted rounded-2xl" />)}
        </div>
      ) : reports.length === 0 ? (
        <Card className="border-0 shadow-md rounded-3xl">
          <CardContent className="py-16 text-center">
            <Shield className="h-14 w-14 mx-auto text-muted-foreground/30 mb-3" />
            <h3 className="font-heading text-lg font-bold mb-1">All clear</h3>
            <p className="text-sm text-muted-foreground">No {tab} reports at the moment.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map(r => (
            <Card key={r.id} className="border-0 shadow-md rounded-2xl" data-testid={`report-${r.id}`}>
              <CardContent className="p-5 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="destructive" className="rounded-full">
                    <Flag className="h-3 w-3 mr-1" />
                    Reported {r.target_type}
                  </Badge>
                  {r.target?.hidden && (
                    <Badge variant="outline" className="rounded-full text-orange-600 border-orange-300">
                      <EyeOff className="h-3 w-3 mr-1" />
                      Hidden
                    </Badge>
                  )}
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <User className="h-3 w-3" /> Reported by {r.reporter_name}
                  </span>
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <Calendar className="h-3 w-3" /> {format(parseISO(r.created_at), "MMM d, yyyy · HH:mm")}
                  </span>
                </div>

                <div className="rounded-xl bg-muted/40 p-3 text-sm">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Reason</p>
                  <p className="italic break-words whitespace-pre-wrap">{r.reason}</p>
                </div>

                <div className="rounded-xl border border-border p-3 text-sm">
                  <p className="text-xs uppercase tracking-wider text-muted-foreground mb-1 flex items-center gap-1.5">
                    {r.target_type === "post" ? <FileText className="h-3 w-3" /> : <MessageSquare className="h-3 w-3" />}
                    {r.target_type === "post" ? "Post" : "Comment"}
                  </p>
                  {r.target ? (
                    <>
                      {r.target.title && <p className="font-semibold mb-1">{r.target.title}</p>}
                      <p className="text-muted-foreground whitespace-pre-wrap line-clamp-6">{r.target.content}</p>
                    </>
                  ) : (
                    <p className="text-xs italic text-muted-foreground">Target no longer exists.</p>
                  )}
                </div>

                {r.status === "pending" && r.target && (
                  <div className="flex flex-wrap gap-2 pt-1">
                    {r.target.hidden ? (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openConfirm(r, "unhide")} data-testid={`mod-unhide-${r.id}`}>
                        <Eye className="mr-1.5 h-3.5 w-3.5" /> Unhide
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => openConfirm(r, "hide")} data-testid={`mod-hide-${r.id}`}>
                        <EyeOff className="mr-1.5 h-3.5 w-3.5" /> Hide
                      </Button>
                    )}
                    <Button size="sm" variant="outline" className="rounded-full" onClick={() => openConfirm(r, "edit")} data-testid={`mod-edit-${r.id}`}>
                      <Edit2 className="mr-1.5 h-3.5 w-3.5" /> Edit
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-full text-destructive hover:text-destructive" onClick={() => openConfirm(r, "delete")} data-testid={`mod-delete-${r.id}`}>
                      <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete
                    </Button>
                    <Button size="sm" variant="ghost" className="rounded-full ml-auto text-muted-foreground" onClick={() => dismissReport(r.id)} data-testid={`mod-dismiss-${r.id}`}>
                      <XCircle className="mr-1.5 h-3.5 w-3.5" /> Dismiss report
                    </Button>
                  </div>
                )}
                {r.status !== "pending" && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    {r.status === "resolved" ? "Resolved" : "Dismissed"}
                    {r.resolved_at && ` · ${format(parseISO(r.resolved_at), "MMM d, yyyy")}`}
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Confirm action dialog */}
      <Dialog open={!!confirmAction} onOpenChange={(o) => { if (!o) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-lg rounded-3xl" data-testid="mod-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading capitalize flex items-center gap-2">
              <Shield className="h-5 w-5 text-primary" />
              {confirmAction?.action} {confirmAction?.report?.target_type}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {confirmAction?.action === "edit" && confirmAction?.report?.target_type === "post" && (
              <div>
                <label className="text-xs font-medium mb-1 block">Title</label>
                <input
                  className="w-full rounded-xl border border-border px-3 py-2 text-sm"
                  value={confirmAction.editTitle}
                  onChange={(e) => setConfirmAction(prev => ({ ...prev, editTitle: e.target.value }))}
                  data-testid="mod-edit-title-input"
                />
              </div>
            )}
            {confirmAction?.action === "edit" && (
              <div>
                <label className="text-xs font-medium mb-1 block">Content</label>
                <Textarea
                  rows={5}
                  className="rounded-xl"
                  value={confirmAction.editContent}
                  onChange={(e) => setConfirmAction(prev => ({ ...prev, editContent: e.target.value }))}
                  data-testid="mod-edit-content-input"
                />
              </div>
            )}
            <div>
              <label className="text-xs font-medium mb-1 block">Reason (visible in audit log)</label>
              <Textarea
                rows={2}
                className="rounded-xl"
                placeholder="Why are you taking this action?"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                data-testid="mod-reason-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2 pt-2">
            <Button variant="outline" className="rounded-full" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button className="rounded-full" onClick={applyAction} data-testid="mod-confirm-btn">
              Confirm {confirmAction?.action}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
